/**
 * Badge — status pill of the Obrava design system (AppFilters · «Badge
 * d'estat»). Mono, uppercase, with a leading dot. The state is ALWAYS a word
 * plus the dot: colour alone never explains it.
 *
 * Tones: `info` (en curs) · `pending` (pendent / incidència) · `done`
 * (completat) · `warning` (avís) · `neutral` (sense estat: tipologia, etc.).
 */

import React from 'react';
import { useTheme } from '../theme/index.js';
import type { Theme } from '../tokens/index.js';

export type BadgeTone = 'info' | 'pending' | 'done' | 'warning' | 'neutral' | 'brand';

export interface BadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'style' | 'children'> {
  readonly tone: BadgeTone;
  /** The state word. Rendered uppercase by CSS; pass it in sentence case. */
  readonly label: string;
  /** Classification describes identity; only operational status carries a dot. */
  readonly kind?: 'status' | 'classification';
  readonly style?: React.CSSProperties;
}

function tonePalette(theme: Theme, tone: BadgeTone): { bg: string; fg: string } {
  const c = theme.color;
  switch (tone) {
    case 'info':
      return { bg: c.infoBg, fg: c.infoText };
    case 'pending':
      return { bg: c.dangerBg, fg: c.dangerText };
    case 'done':
      return { bg: c.successBg, fg: c.successText };
    case 'warning':
      return { bg: c.warningBg, fg: c.warningText };
    case 'brand':
      return { bg: c.accentSubtle, fg: c.accent };
    case 'neutral':
      return { bg: c.neutralBg, fg: c.neutralText };
    default: {
      const exhaustive: never = tone;
      return exhaustive;
    }
  }
}

export function Badge(props: BadgeProps): React.ReactElement {
  const { tone, label, kind = 'status', style, ...rest } = props;
  const theme = useTheme();
  const { bg, fg } = tonePalette(theme, tone);
  const { space } = theme.spacing;

  const composed: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: space[2],
    height: 24,
    paddingInlineStart: kind === 'classification' ? space[4] : 10,
    paddingInlineEnd: space[4],
    backgroundColor: bg,
    color: fg,
    borderRadius: theme.radii.radius.pill,
    fontFamily: theme.typography.family.mono,
    fontSize: theme.typography.size.caption,
    lineHeight: '16px',
    fontWeight: theme.typography.weight.regular,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
    ...style,
  };

  return React.createElement(
    'span',
    { ...rest, style: composed },
    kind === 'status' ? React.createElement('span', {
      'aria-hidden': true,
      style: {
        width: 6,
        height: 6,
        borderRadius: theme.radii.radius.pill,
        backgroundColor: 'currentColor',
        flex: 'none',
      } satisfies React.CSSProperties,
    }) : null,
    label,
  );
}
